import type { OrderView } from "@townsy/contracts";
import {
  KIND_LABEL,
  ORDER_STATUS_LABEL,
  ORDER_STATUS_TONE,
  PAYMENT_STATUS_LABEL,
  REFUND_STATUS_LABEL,
  canPayNow,
  fundingLines,
} from "../../commerce/orderStatus";
import { billboardName } from "../../advertise/media";
import { formatUtcPeriod } from "../../advertise/utcDays";
import { formatCents, formatDateTime } from "../../lib/format";
import { formatCountdown, msUntil } from "../../lib/serverClock";

export const TEST_MODE_RECEIPT_NOTE = "Stripe TEST mode: no real money moved. Test-mode purchases are development data.";

/**
 * One order as the server reports it (= the receipt, ADR-0007 #13): status headline, the server's
 * plain-language message verbatim, amounts by funding source (Townsy Credit and card are separate
 * lines), sales tax as its own line (ADR-0046: card-paid, never Townsy Credit), card payment status,
 * refund status (shown exactly as reported — never "refunded" before the server says so), receipt
 * number, and the test-mode label (only for a test-mode order).
 */
export function OrderStatusView({
  order,
  now,
  compact = false,
  modeBadge = true,
}: {
  order: OrderView;
  now: number;
  compact?: boolean;
  /** The checkout dialog already shows "Test mode" in its header. */
  modeBadge?: boolean;
}) {
  const f = fundingLines(order);
  const tone = ORDER_STATUS_TONE[order.status];
  const holdMs = msUntil(order.reservationExpiresAt, now);
  const showHold = canPayNow(order, now);
  const live = order.mode === "live";
  const stripeTax = order.tax?.source === "stripe_tax";
  const taxLines = (order.tax?.lines ?? []).filter((l) => l.amountCents > 0);
  const taxPlace = [order.tax?.country, order.tax?.postalCode].filter(Boolean).join(" ");
  return (
    <div className="stack order-status" data-testid="order-status" data-status={order.status}>
      <div className="row" style={{ gap: 6 }}>
        <span className={`badge order-badge order-badge-${tone}`} data-testid="order-status-label">
          {ORDER_STATUS_LABEL[order.status]}
        </span>
        {modeBadge && !live ? (
          <span className="badge badge-warning" data-testid="test-mode-badge">
            Test mode
          </span>
        ) : null}
      </div>
      <p className={`notice notice-${tone === "danger" ? "danger" : tone === "warning" ? "warning" : tone === "success" ? "success" : "info"}`} role="status" style={{ margin: 0 }} data-testid="order-message">
        {order.message}
      </p>
      <dl className="kv">
        <dt>Purchase</dt>
        <dd>
          <strong>{order.description || KIND_LABEL[order.kind]}</strong>
          <div className="small muted">{KIND_LABEL[order.kind]}</div>
        </dd>
        {order.billboard ? (
          <>
            <dt>Billboard</dt>
            <dd data-testid="order-billboard">{billboardName(order.billboard.billboardId)}</dd>
            <dt>Dates</dt>
            <dd data-testid="order-billboard-dates">{formatUtcPeriod(order.billboard.startDate, order.billboard.endDate)}</dd>
          </>
        ) : null}
        {order.kind === "CAMPAIGN_FUNDING" ? (
          <>
            <dt>Campaign</dt>
            <dd data-testid="order-campaign">
              Sponsored task budget{" "}
              <span className="small muted">· card only: Townsy Credit never funds a campaign</span>
            </dd>
          </>
        ) : null}
        <dt>Receipt</dt>
        <dd data-testid="order-receipt">{order.receiptNumber ?? <span className="muted">Issued when the purchase completes</span>}</dd>
        {!compact ? (
          <>
            <dt>Order</dt>
            <dd className="small mono">{order.orderId}</dd>
            <dt>Placed</dt>
            <dd>{formatDateTime(order.createdAt)}</dd>
            {order.fulfilledAt ? (
              <>
                <dt>Completed</dt>
                <dd>{formatDateTime(order.fulfilledAt)}</dd>
              </>
            ) : null}
          </>
        ) : null}
        <dt>Price</dt>
        <dd className="price">{formatCents(f.subtotalCents)}</dd>
        <dt>{stripeTax ? "Sales tax" : "Tax"}</dt>
        <dd className="price" data-testid="order-tax">
          {formatCents(f.taxCents)}
          {stripeTax && taxPlace ? <span className="small muted"> · {taxPlace}</span> : null}
          {taxLines.length ? (
            <div className="small muted" data-testid="order-tax-lines">
              {taxLines.map((l) => `${l.label}: ${formatCents(l.amountCents)}`).join("; ")}
            </div>
          ) : null}
        </dd>
        {f.taxCents > 0 ? (
          <>
            <dt>Total</dt>
            <dd className="price" data-testid="order-total">
              {formatCents(f.subtotalCents + f.taxCents)}
            </dd>
          </>
        ) : null}
        <dt>Townsy Credit</dt>
        <dd className="price" data-testid="order-credit">
          {formatCents(f.creditCents)}
        </dd>
        <dt>{live ? "Card" : "Card (test)"}</dt>
        <dd className="price" data-testid="order-card">
          {formatCents(f.cardCents)}
        </dd>
        {f.cardCents > 0 ? (
          <>
            <dt>Card payment</dt>
            <dd data-testid="order-payment-status">{PAYMENT_STATUS_LABEL[order.paymentStatus]}</dd>
          </>
        ) : null}
        {order.refund.status !== "none" ? (
          <>
            <dt>Refund</dt>
            <dd data-testid="order-refund">
              {REFUND_STATUS_LABEL[order.refund.status]}
              {order.refund.amountCents > 0 ? ` · ${formatCents(order.refund.amountCents)} to your card` : ""}
            </dd>
          </>
        ) : null}
        {showHold ? (
          <>
            <dt>Held for you</dt>
            <dd data-testid="order-hold">{formatCountdown(holdMs)} left</dd>
          </>
        ) : null}
      </dl>
      <p className="hint" style={{ margin: 0 }} data-testid="order-note">
        {live ? "" : `${TEST_MODE_RECEIPT_NOTE} `}
        {f.taxCents > 0 ? "Sales tax is paid by card; Townsy Credit is never used for tax. " : ""}Townsy Credit is not cash and cannot be withdrawn.
        {order.status === "compensation_required" || order.status === "refund_pending"
          ? " A refund goes back to the card that paid; it is never replaced by Townsy Credit."
          : ""}
      </p>
    </div>
  );
}
